import Kichiri from "./KichiriService";
import UserService from "./UserService";
import UtilityService from "./UtilityService";
import ClientService from "./ClientService";
import ConfigurationService from "./ConfigurationService";
import { authedFetch } from "./apiFetch";

function computeBankMovementsByAccount(transactions) {
	const movementsByAccount = {};

	for (const transaction of transactions || []) {
		const classification = transaction.classification;
		if (!classification || classification === "Unclassified") continue;

		const amount = Number(transaction.amount) || 0;
		if (amount === 0) continue;

		if (transaction.bankAccountNumber) {
			const bankTotal = movementsByAccount[transaction.bankAccountNumber] || 0;
			movementsByAccount[transaction.bankAccountNumber] = bankTotal + amount;
		}

		const accountNumber = UtilityService.getLeadingNumber(classification);
		if (accountNumber) {
			const accountTotal = movementsByAccount[accountNumber] || 0;
			movementsByAccount[accountNumber] = accountTotal - amount;
		}
	}

	return movementsByAccount;
}

function computeAjeMovementsByAccount(adjustingEntries) {
	const movementsByAccount = {};

	for (const entry of adjustingEntries || []) {
		for (const line of entry.lines || []) {
			const amount = Math.abs(Number(line.amount) || 0);
			if (amount === 0 || !line.account) continue;

			const signedAmount = line.side === "DEBIT" ? amount : -amount;
			const accountTotal = movementsByAccount[line.account] || 0;
			movementsByAccount[line.account] = accountTotal + signedAmount;
		}
	}

	return movementsByAccount;
}

function computeClosingBalance({ isCanonical, accountNumber, current, opening, movements, aje }) {
	if (isCanonical) return current || 0;
	if (accountNumber === "NETINC") return current;
	return opening + movements + aje;
}

function buildAccountRow({ account, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount }) {
	const isCanonical = !account.schedule;
	const sign = account.sign || 1;
	const opening = isBalanceSheet ? (account.prior || 0) : 0;

	const rawBankMovement = isCanonical ? 0 : (bankMovementsByAccount[account.accountNumber] || 0);
	const rawAjeMovement = ajeMovementsByAccount[account.accountNumber] || 0;
	const movements = isCanonical ? (account.current || 0) : rawBankMovement * sign;
	const aje = isCanonical ? 0 : rawAjeMovement * sign;
	const closing = computeClosingBalance({ isCanonical, accountNumber: account.accountNumber, current: account.current, opening, movements, aje });
	const prior = account.prior || 0;
	const changePercent = UtilityService.calculatePercentChange({ from: prior, to: closing });

	return { account, description: account.description, accountNumber: account.accountNumber, opening, movements, aje, closing, prior, changePercent };
}

function buildSectionRows({ section, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount }) {
	const accounts = section.accounts.map(account => buildAccountRow({ account, isBalanceSheet, bankMovementsByAccount, ajeMovementsByAccount }));
	return { name: section.name, accounts };
}

const AFSService = {
	/**
	 * Builds the trial balance rows a working paper shows — every account with its
	 * opening balance, bank movements, AJE movements, closing balance, prior-year
	 * figure and the percentage change — grouped by balance sheet then income
	 * statement section. Pure: no network, no mutation of the inputs.
	 *
	 * @param {Object} balanceSheet - { sections: [{ name, accounts }] }
	 * @param {Object} incomeStatement - { sections: [{ name, accounts }] }
	 * @param {Array} transactions - Bank transactions
	 * @param {Array} adjustingEntries - AJEs with debit/credit lines
	 * @returns {Array} sections: [{ name, accounts: [{ account, description, accountNumber, opening, movements, aje, closing, prior, changePercent }] }]
	 */
	buildTrialBalanceRows({ balanceSheet, incomeStatement, transactions, adjustingEntries }) {
		const balanceSheetSections = (balanceSheet && balanceSheet.sections) || [];
		const incomeStatementSections = (incomeStatement && incomeStatement.sections) || [];

		const bankMovementsByAccount = computeBankMovementsByAccount(transactions);
		const ajeMovementsByAccount = computeAjeMovementsByAccount(adjustingEntries);

		const balanceSheetRows = balanceSheetSections.map(section => buildSectionRows({ section, isBalanceSheet: true, bankMovementsByAccount, ajeMovementsByAccount }));
		const incomeStatementRows = incomeStatementSections.map(section => buildSectionRows({ section, isBalanceSheet: false, bankMovementsByAccount, ajeMovementsByAccount }));

		const sections = [...balanceSheetRows, ...incomeStatementRows];
		return sections;
	},

	/**
	 * Generates AFS balance sheet and income statement from transactions
	 * @param {Number} clientId
	 * @param {Array} transactions
	 * @param {String} yearEnd
	 * @param {String} province
	 * @param {String} accountantName
	 * @param {String} firmName
	 * @return {Promise<Object>}
	 */
	async generateAFS({ clientId, transactions, yearEnd, province, accountantName, firmName }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.generateAFS({ clientId, transactions, yearEnd, province, accountantName, firmName }, {}, user.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Uploads AFS source files for a client
	 * @param {Number} clientId
	 * @param {Array<File>} files
	 * @return {Promise<Object>}
	 */
	async uploadAFSFiles({ clientId, files }) {
		const formData = new FormData();
		for (const f of (files || [])) {
			formData.append("files", f);
		}

		const response = await authedFetch(`/tools/afs/upload?clientId=${clientId}`, {
			method: "POST",
			body: formData,
		});

		return response.json();
	},

	/**
	 * Identifies bank accounts from uploaded statement files
	 * @param {Number} clientId
	 * @param {Array<File>} files
	 * @return {Promise<Object>}
	 */
	async identifyBankAccounts({ clientId, files }) {
		const formData = new FormData();
		for (const f of (files || [])) {
			formData.append("files", f);
		}

		const response = await authedFetch(`/tools/bookkeeping/identify_bank_accounts?clientId=${clientId}`, {
			method: "POST",
			body: formData,
		});

		return response.json();
	},

	/**
	 * Downloads the generated AFS as an Excel file
	 * @param {Number} clientId
	 * @param {Object} balanceSheet
	 * @param {Object} incomeStatement
	 * @param {String} yearEnd
	 * @return {Promise<void>}
	 */
	async downloadAFS({ clientId, balanceSheet, incomeStatement, yearEnd }) {
		const { client } = await ClientService.fetchClient({ clientId });

		const response = await authedFetch(`/tools/afs/download`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ clientId, balanceSheet, incomeStatement, yearEnd }),
		});

		const blob = await response.blob();
		UtilityService.downloadFile({ blob, fileName: `${client.name} AFS.xlsx` });
	},

	/**
	 * Full CSRS 4200 compilation pipeline — SSE streaming.
	 * Sends transactions + settings → classifies → validates → builds AFS → streams review agent.
	 *
	 * Events emitted: { type: "state", state, afs, ... } then agent tool events, then { type: "final" }
	 */
	runCompilationEngagement({ clientId, province, yearEnd, accountantName, firmName, onEvent, onState, onComplete, onError }) {
		const controller = new AbortController();
		let finished = false;
		let pausedOnSuspense = false;

		const finishWithError = message => {
			if (finished) return;
			finished = true;
			if (onError) onError(message);
		};
		const finishWithComplete = data => {
			if (finished) return;
			finished = true;
			if (onComplete) onComplete(data);
		};

		authedFetch(`/tools/afs/compilation_run`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			signal: controller.signal,
			body: JSON.stringify({
				client_id: clientId,
				province: province || undefined,
				fiscal_year_end: yearEnd || undefined,
				accountant_name: accountantName || undefined,
				firm_name: firmName || undefined,
			}),
		}).then(function (response) {
			if (!response.ok) {
				response.text()
					.then(text => finishWithError(`AFS build failed (HTTP ${response.status}): ${(text || "").slice(0, 300)}`))
					.catch(() => finishWithError(`AFS build failed (HTTP ${response.status})`));
				return;
			}

			let contentType = response.headers.get("content-type") || "";

			if (contentType.includes("application/json")) {
				response.json().then(function (data) {
					if (onState) onState(data);
					finishWithComplete(data);
				}).catch(function (err) {
					finishWithError(err.message);
				});
				return;
			}

			let reader = response.body.getReader();
			let decoder = new TextDecoder();
			let buffer = "";

			function readChunk() {
				reader.read().then(function ({ done, value }) {
					if (done) {
						if (!pausedOnSuspense) {
							finishWithError("The AFS build ended before finishing — the server may have hit an error. Check the API logs.");
						}
						return;
					}

					buffer += decoder.decode(value, { stream: true });
					let lines = buffer.split("\n");
					buffer = lines.pop();

					for (let line of lines) {
						if (line.startsWith("data: ")) {
							try {
								let event = JSON.parse(line.substring(6));
								if (event.type === "final") {
									finishWithComplete(event.result);
								} else if (event.type === "error") {
									finishWithError(event.message);
								} else if (event.type === "state") {
									if (event.unresolvedItems && event.unresolvedItems.length > 0) pausedOnSuspense = true;
									if (onState) onState(event);
								} else {
									if (onEvent) onEvent(event);
								}
							} catch (e) {}
						}
					}

					readChunk();
				}).catch(function (err) {
					if (err && err.name === "AbortError") return;
					finishWithError(err.message);
				});
			}

			readChunk();
		}).catch(function (err) {
			if (err && err.name === "AbortError") return;
			finishWithError(err.message);
		});

		return {
			abort: () => {
				finished = true;
				controller.abort();
			},
		};
	},

	/**
	 * Fetches the documentation checklist for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async fetchDocumentationChecklist({ clientId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.documentationChecklist({ client_id: clientId }, {}, user.authToken);
		return response.data;
	},

	/**
	 * Asks for a document to be checked again after an attempt that produced no answer
	 * @param {Number} documentId
	 * @return {Promise<Object>}
	 */
	async verifyDocumentAgain({ documentId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.verifyDocumentAgain({ documentId: documentId }, {}, user.authToken);
		return response.data;
	},

	/**
	 * Clears the agent mode state for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async clearAgentMode({ clientId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.agentModeClear({ client_id: clientId }, {}, user.authToken);
		return response.data;
	},

	/**
	 * Build the URL for streaming a source document inline. The iframe in
	 * DocumentViewerModal points at this URL — the backend handler returns
	 * the file with Content-Disposition: inline so the browser renders it
	 * (PDFs natively, images / text inline). authToken is in the query
	 * string because iframes can't send headers.
	 */
	getDocumentViewUrl({ evidenceItemId, documentId }) {
		let user = UserService.getUserData();
		let base = `${ConfigurationService.resolveApi().rest}/tools/afs/document_view`;
		let qs = [];
		if (evidenceItemId != null) qs.push("evidenceItemId=" + encodeURIComponent(evidenceItemId));
		if (documentId != null) qs.push("documentId=" + encodeURIComponent(documentId));
		if (user && user.authToken) qs.push("authToken=" + encodeURIComponent(user.authToken));
		return base + (qs.length > 0 ? "?" + qs.join("&") : "");
	},

	/**
	 * Fetches the agent mode manifest for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async fetchAgentModeManifest({ clientId }) {
		let user = UserService.getUserData();
		let response = await Kichiri.tools.agentModeManifest({ client_id: clientId }, {}, user.authToken);
		return response.data.manifest || null;
	},

	/**
	 * Runs agent mode for a client, streaming events via callbacks
	 * @param {Number} clientId
	 * @param {Function} onEvent
	 * @param {Function} onComplete
	 * @param {Function} onError
	 * @return {void}
	 */
	runAgentMode({ clientId, onEvent, onComplete, onError }) {
		authedFetch(`/tools/afs/agent_mode_run`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ client_id: clientId }),
		}).then(function (response) {
			let reader = response.body.getReader();
			let decoder = new TextDecoder();
			let buffer = "";

			function readChunk() {
				reader.read().then(function ({ done, value }) {
					if (done) return;

					buffer += decoder.decode(value, { stream: true });
					let lines = buffer.split("\n");
					buffer = lines.pop();

					for (let line of lines) {
						if (line.startsWith("data: ")) {
							try {
								let event = JSON.parse(line.substring(6));
								if (event.type === "final") {
									if (onComplete) onComplete(event.manifest);
								} else if (event.type === "error") {
									if (onError) onError(event.message);
								} else {
									if (onEvent) onEvent(event);
								}
							} catch (e) {}
						}
					}

					readChunk();
				}).catch(function (err) {
					if (onError) onError(err.message);
				});
			}

			readChunk();
		}).catch(function (err) {
			if (onError) onError(err.message);
		});
	},

	/**
	 * Downloads the compilation report as a PDF file
	 * @param {Number} clientId
	 * @param {String} entityName
	 * @param {String} yearEnd
	 * @param {Object} balanceSheet
	 * @param {Object} incomeStatement
	 * @param {Object} retainedEarnings
	 * @param {Object} compilationData
	 * @param {Object} clientProfile
	 * @param {Object} ppeNote
	 * @param {Object} noteTexts
	 * @param {Array} noteIds
	 * @return {Promise<void>}
	 */
	async downloadPDF({ clientId, entityName, yearEnd, balanceSheet, incomeStatement, retainedEarnings, compilationData, clientProfile, ppeNote, noteTexts, noteIds, customNoteTitles }) {
		try {
			let { client } = await ClientService.fetchClient({ clientId });

			let response = await authedFetch(`/tools/afs/download_pdf`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					clientId,
					entityName: entityName || client.name,
					yearEnd,
					balanceSheet,
					incomeStatement,
					retainedEarnings,
					compilationData,
					clientProfile,
					ppeNote,
					noteTexts: noteTexts || {},
				noteIds: noteIds || undefined,
				customNoteTitles: customNoteTitles || undefined,
				}),
			});

			let blob = await response.blob();
			UtilityService.downloadFile({ blob, fileName: `${client.name} - Compilation ${yearEnd || "AFS"}.pdf` });
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Generates an AI review of the AFS financial statements
	 * @param {Number} clientId
	 * @param {String} entityName
	 * @param {String} yearEnd
	 * @param {Object} balanceSheet
	 * @param {Object} incomeStatement
	 * @param {Object} retainedEarnings
	 * @return {Promise<Object>}
	 */
	async generateReview({ clientId, entityName, yearEnd, balanceSheet, incomeStatement, retainedEarnings }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.reviewAFS({ clientId, entityName, yearEnd, balanceSheet, incomeStatement, retainedEarnings }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Retrieves the compliance checklist for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getComplianceChecklist({ clientId }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.complianceChecklist({ client_id: clientId }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Sets or clears a compliance evidence value for a client
	 * @param {Number} clientId
	 * @param {String} storage_key
	 * @param {*} value
	 * @param {Boolean} clear
	 * @return {Promise<Object>}
	 */
	async setComplianceEvidence({ clientId, storage_key, value, clear = false }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.complianceEvidence({ client_id: clientId, storage_key: storage_key, value: value, clear: clear }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Uploads a compliance document for a client
	 * @param {Number} clientId
	 * @param {String} storage_key
	 * @param {File} file
	 * @return {Promise<Object>}
	 */
	async uploadComplianceDocument({ clientId, storage_key, file }) {
		try {
			let formData = new FormData();
			formData.append("files", file);
			formData.append("clientId", clientId);
			formData.append("storage_key", storage_key);
			let response = await authedFetch(`/tools/afs/compliance_document`, {
				method: "POST",
				body: formData,
			});
			return await response.json();
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Deletes a compliance document for a client
	 * @param {Number} clientId
	 * @param {String} storage_key
	 * @return {Promise<Object>}
	 */
	async deleteComplianceDocument({ clientId, storage_key }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.deleteComplianceDocument({ client_id: clientId, storage_key: storage_key }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Acknowledges or unacknowledges a compliance item for a client
	 * @param {Number} clientId
	 * @param {Number} ciId
	 * @param {Boolean} acknowledge
	 * @param {String} note
	 * @return {Promise<Object>}
	 */
	async acknowledgeCi({ clientId, ciId, acknowledge = true, note = null }) {
		try {
			let user = UserService.getUserData();
			let response = await Kichiri.tools.complianceAcknowledge({ client_id: clientId, ci_id: ciId, acknowledge: acknowledge, note: note }, {}, user.authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},
};

export default AFSService;
